import React, { useEffect, useState } from "react";
import {
  addDays,
  addMonths,
  eachDayOfInterval,
  endOfMonth,
  format,
  getDay,
  isSameDay,
  isToday,
  startOfMonth,
  subDays,
  subMonths,
  isSameMonth
} from "date-fns";
import { useTranslation } from "react-i18next";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import { IconButton, Popover } from "@mui/material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import { swapCalendar } from "../../../axios/Eventlog/EventLogApi";
import { useAuth } from "../../../context/AuthContext";
import { useLoading } from "../../../context/LoadingContext";
import { DateTime } from "luxon";
import ClockWarning16pxSvg from "../../../assets/images/icons/ClockWarning16pxSvg";
import CloseReject16pxSvg from "../../../assets/images/icons/CloseReject16pxSvg";
import TickApporve16pxSvg from "../../../assets/images/icons/TickApporve16pxSvg";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

const ShiftEventsCalendar = (props) => {
  const {
    setSelectedDate,
    selectedDate,
    calculateDisplayedWeeks,
    selectedUser, selectedGroup, selectedShift, selectedStatus, refreshData
  } = props;

  const { t, i18n } = useTranslation();

  const [monthCalendarItemData] = useState([]);
  const [calendarDataApi, setCalendarDataApi] = useState([]);
  const { profile, isAdmin, isEmployee, genaralSettingTimeZone, genaralSettingDateFormat } = useAuth();
  const [show, hide] = useLoading();

  const months = [
    t("common.january"),
    t("common.february"),
    t("common.march"),
    t("common.april"),
    t("common.may"),
    t("common.june"),
    t("common.july"),
    t("common.august"),
    t("common.september"),
    t("common.october"),
    t("common.november"),
    t("common.december"),
  ];

  const weekDays = [
    t("common.monday3"),
    t("common.tuesday3"),
    t("common.wednesday3"),
    t("common.thursday3"),
    t("common.friday3"),
    t("common.saturday3"),
    t("common.sunday3"),
  ];

  const currentMonthByText = months[selectedDate.getMonth()];
  const currentYear = selectedDate.getFullYear();

  useEffect(() => {
    if (selectedDate) {
      const currentDate = new Date(selectedDate);
      calculateDisplayedWeeks(currentDate, weekDays[0]);
    }
  }, [selectedDate, weekDays]);

  const [currentDate, setCurrentDate] = useState(new Date());

  const getMonthRangeByTZ = (date, timeZone) => {
    const base = DateTime.fromJSDate(date, {
      zone: timeZone || "UTC",
    });

    return {
      from: base.startOf("month").toUTC().toISO(),
      to: base.endOf("month").toUTC().toISO(),
    };
  };

  useEffect(() => {
    const fetchChart = async () => {

      try {
        show();
        const companyTimezone = genaralSettingTimeZone;

        const { from, to } = getMonthRangeByTZ(selectedDate, genaralSettingTimeZone);

        const payload = {
          accountId: (isEmployee ? profile?.accountId : null),
          userId: selectedUser?.id || null,
          groupId: selectedGroup || null,
          shiftId: selectedShift || null,
          requestStatusId: selectedStatus || null,
          from: from,
          to: to
        };

        const res = await swapCalendar(payload);

        setCalendarDataApi(res?.data?.data || []);
      } catch (err) {
        console.error(err);
      } finally {
        hide();
      }
    };

    if (selectedDate) {
      fetchChart();
    }
  }, [
    selectedUser?.id,
    selectedGroup,
    selectedShift,
    selectedDate,
    selectedStatus,
    refreshData
  ]);

  useEffect(() => {
    if (!selectedDate) return;
    if (!isSameMonth(selectedDate, currentDate)) {
      setCurrentDate(startOfMonth(selectedDate));
    }
  }, [selectedDate]);

  const getEventsForDay = (day) => {
    const formattedDay = format(day, "yyyy-MM-dd");
    return monthCalendarItemData.filter(
      (event) => event.date === formattedDay
    );
  };

  const firstDayOfMonth = startOfMonth(currentDate);
  const lastDayOfMonth = endOfMonth(currentDate);
  const daysInMonth = eachDayOfInterval({
    start: firstDayOfMonth,
    end: lastDayOfMonth,
  });

  const startingDateIndex =
    getDay(firstDayOfMonth) === 0 ? 6 : getDay(firstDayOfMonth) - 1;

  const getNextMonthDays = (lastDayOfMonth, emptyCell) => {
    const nextMonthDays = [];
    for (let i = 1; i <= emptyCell; i++) {
      nextMonthDays.push(addDays(lastDayOfMonth, i));
    }
    return nextMonthDays;
  };

  const getPrevMonthDays = (firstDayOfMonth, emptyCell) => {
    const prevMonthDays = [];
    for (let i = 1; i <= emptyCell; i++) {
      prevMonthDays.unshift(subDays(firstDayOfMonth, i));
    }
    return prevMonthDays;
  };

  const totalDays = daysInMonth.length + startingDateIndex;
  const totalCells = totalDays > 35 ? 42 : 35;

  const emptyCellsForNextMonth =
    totalCells - (daysInMonth.length + startingDateIndex);

  const nextMonthDays = getNextMonthDays(
    lastDayOfMonth,
    emptyCellsForNextMonth
  );

  const prevMonthDays = getPrevMonthDays(firstDayOfMonth, startingDateIndex);

  const handlePreviousMonth = () => {
    setCurrentDate(subMonths(currentDate, 1));
    setSelectedDate(subMonths(selectedDate, 1));
  };

  const handlePrevMonth = () => {
    setCurrentDate(prev =>
      new Date(prev.getFullYear(), prev.getMonth() - 1, 1)
    );
    setSelectedDate(subMonths(selectedDate, 1));
  };

  const handleNextMonth = () => {
    setCurrentDate(prev =>
      new Date(prev.getFullYear(), prev.getMonth() + 1, 1)
    );
    setSelectedDate(addMonths(selectedDate, 1));
  };

  // const handleNextMonth = () => {
  //   setCurrentDate(addMonths(currentDate, 1));
  //   setSelectedDate(addMonths(selectedDate, 1));
  // };

  const handleSelectDay = (day) => {
    setSelectedDate(day);
  };

  const handleMonthYearChange = (newDate) => {
    setCurrentDate(newDate);
    setSelectedDate(newDate);
  };

  const legendData = React.useMemo(() => {
    return [
      {
        color: <div className="rounded-full w-3 h-3 bg-[#0057FF]" />,
        display: t("common.current-day"),
      },
      {
        color: <div className="rounded-full w-3 h-3 bg-[#0EA5E9]" />,
        display: t("common.selected-day"),
      },
      {
        color: <div className="rounded-full w-3 h-3 bg-[#058900]" />,
        display: t("common.approved"),
      },
      {
        color: <div className="rounded-full w-3 h-3 bg-[#D00D00]" />,
        display: t("common.rejected"),
      },
      {
        color: <div className="rounded-full w-3 h-3 bg-[#F97C00]" />,
        display: t("common.requested"),
      },
    ];
  }, [i18n.language, t]);

  const MonthYearPicker = ({ selectedDate, onChange }) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const [year, setYear] = useState(selectedDate.getFullYear());

    const handleOpen = (e) => setAnchorEl(e.currentTarget);
    const handleClose = () => setAnchorEl(null);
    const open = Boolean(anchorEl);

    const handleSelect = (monthIndex) => {
      const newDate = new Date(year, monthIndex, 1);
      onChange(newDate);
      handleClose();
    };

    return (
      <>
        <button
          onClick={handleOpen}
          className="flex items-center gap-1 border px-3 py-1 rounded shadow-sm bg-white hover:bg-gray-100 h-8"
        >
          <span className="text-[#49494B] text-sm">
            {months[selectedDate.getMonth()]} {selectedDate.getFullYear()}
          </span>
          <ArrowDropDownIcon fontSize="small" />
        </button>

        <Popover open={open} anchorEl={anchorEl} onClose={handleClose}>
          <div className="p-4 w-72">
            <div className="flex justify-between items-center mb-3">
              <button onClick={() => setYear(year - 1)}>
                <ArrowBackIosNewIcon fontSize="small" />
              </button>
              <span className="font-medium">{year}</span>
              <button onClick={() => setYear(year + 1)}>
                <ArrowForwardIosIcon fontSize="small" />
              </button>
            </div>

            <div className="grid grid-cols-3 gap-2">
              {months.map((month, index) => (
                <button
                  key={index}
                  className={`py-1 rounded text-sm ${selectedDate.getMonth() === index &&
                    selectedDate.getFullYear() === year
                    ? "bg-blue-600 text-white"
                    : "hover:bg-gray-200"
                    }`}
                  onClick={() => handleSelect(index)}
                >
                  {month}
                </button>
              ))}
            </div>
          </div>
        </Popover>
      </>
    );
  };

  const calendarMap = React.useMemo(() => {
    const map = {};

    if (!Array.isArray(calendarDataApi)) return map;

    calendarDataApi.forEach((item) => {
      if (!item?.day) return;

      const date = item.day.split(" ")[0];

      map[date] = {
        approved: item.sumRequestApproved || 0,
        pending: item.sumRequestPendding || 0,
        rejected: item.sumRequestRejected || 0,
      };
    });

    return map;
  }, [calendarDataApi]);

  return (
    <div className="month-calendar rounded-[10px] px-3 sm:px-3 md:px-3 py-3 ">
      {/* <div className="flex justify-between items-center">
        <MonthYearPicker
          selectedDate={currentDate}
          onChange={handleMonthYearChange}
        />
        <div className="flex gap-1 items-center">
          <IconButton size="small" onClick={handlePreviousMonth}>
            <ArrowBackIosNewIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" onClick={handleNextMonth}>
            <ArrowForwardIosIcon fontSize="small" />
          </IconButton>
        </div>
      </div> */}

      <div className="relative h-10 flex items-center">
        <IconButton
          size="small"
          onClick={handlePrevMonth}
          className="!absolute left-0"
        >
          <ChevronLeftIcon />
        </IconButton>

        <div className="w-full text-center px-8 sm:px-10">
          <p className="text-base sm:text-lg md:text-xl text-[#1F2937] font-semibold">
            {currentMonthByText}, {currentYear}
          </p>
        </div>

        <IconButton
          size="small"
          onClick={handleNextMonth}
          className="!absolute right-0"
        >
          <ChevronRightIcon />
        </IconButton>
      </div>



      {/* Calendar Grid */}
      <div className="mt-4">
        <div className="grid grid-cols-7 text-center">
          {weekDays.map((day) => (
            <div key={day}>
              <p className="uppercase font-semibold text-[10px] sm:text-xs text-[#49494B]">
                {day}
              </p>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-7 text-center border-t border-l border-[#DBE8FF]">
          {prevMonthDays.map((day, index) => (
            <div key={index} className="day prev-month border-r border-b border-[#DBE8FF]">
              <div className="text-xs">{format(day, "d")}</div>
            </div>
          ))}

          {daysInMonth.map((day, index) => {
            const isSelectedDay =
              selectedDate && isSameDay(day, selectedDate);
            const isCurrentDay = isToday(day);
            const eventsForDay = getEventsForDay(day);
            const showNoData = eventsForDay.length === 0;
            const formattedDay = format(day, "yyyy-MM-dd");
            const dayData = calendarMap[formattedDay] || {
              approved: 0,
              pending: 0,
              rejected: 0,
            };

            const statusList = [
              { type: "approved", value: dayData.approved },
              { type: "pending", value: dayData.pending },
              { type: "rejected", value: dayData.rejected },
            ];

            const maxStatus = statusList
              .filter((s) => s.value > 0)
              .sort((a, b) => b.value - a.value)[0];

            return (
              <div
                key={index}
                className="day border-r border-b border-[#DBE8FF] hover:bg-gray-100 cursor-pointer transition"
                style={
                  isSelectedDay
                    ? { backgroundColor: "#F5F8FF", border: "1px solid #0EA5E9" }
                    : isCurrentDay
                    ? {
                        backgroundColor: "#F5F8FF",
                        
                      }
                    : undefined
                }
                onClick={() => handleSelectDay(day)}
              >
                <div className="text-xs font-medium">
                  {format(day, "d")}
                </div>

                {dayData.approved > 0 || dayData.pending > 0 || dayData.rejected > 0 ? (
                  <div className="border border-[#E5E7EB] bg-[#FFFFFF] rounded-md min-h-[22px] w-full flex items-center justify-center mt-1 px-1">

                    {/* DESKTOP */}
                    <div className="hidden md:flex gap-2 items-center">
                      {dayData.approved > 0 && (
                        <p className="flex items-center gap-1 text-[10px] text-[#058900] font-semibold">
                          <TickApporve16pxSvg /> {dayData.approved}
                        </p>
                      )}

                      {dayData.pending > 0 && (
                        <p className="flex items-center gap-1 text-[10px] text-[#F97C00] font-semibold">
                          <ClockWarning16pxSvg /> {dayData.pending}
                        </p>
                      )}

                      {dayData.rejected > 0 && (
                        <p className="flex items-center gap-1 text-[10px] text-[#D00D00] font-semibold">
                          <CloseReject16pxSvg /> {dayData.rejected}
                        </p>
                      )}
                    </div>

                    {/* MOBILE */}
                    <div className="flex md:hidden">
                      {maxStatus?.type === "approved" && (
                        <p className="flex items-center gap-1 text-[10px] text-[#058900] font-semibold">
                          <TickApporve16pxSvg /> {maxStatus.value}
                        </p>
                      )}

                      {maxStatus?.type === "pending" && (
                        <p className="flex items-center gap-1 text-[10px] text-[#F97C00] font-semibold">
                          <ClockWarning16pxSvg /> {maxStatus.value}
                        </p>
                      )}

                      {maxStatus?.type === "rejected" && (
                        <p className="flex items-center gap-1 text-[10px] text-[#D00D00] font-semibold">
                          <CloseReject16pxSvg /> {maxStatus.value}
                        </p>
                      )}
                    </div>

                  </div>
                ) : (
                  <div className="border border-[#E5E7EB] rounded-md h-[22px] w-full flex items-center justify-center mt-1">
                    <p className="text-[10px] text-[#E5E7EB]">
                      {t("common.content-122")}
                    </p>
                  </div>
                )}
              </div>
            );
          })}

          {nextMonthDays.map((day, index) => (
            <div key={index} className="day next-month border-r border-b border-[#DBE8FF]">
              <div className="text-xs">{format(day, "d")}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Legend */}
      <div className="flex justify-center pt-3 flex-wrap gap-[8px]">
        {legendData.map((value, index) => (
          <div className="flex gap-1 items-center" key={index}>
            {value.color}
            <p className="text-xs">{value.display}</p>
          </div>
        ))}
      </div>
    </div >
  );
};

export default ShiftEventsCalendar;
